import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../../../../shared/components/general/Button';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';
import { PmsProcedureTypeModel } from '../../../../../../shared/models/organizations/PmsLookupModels';
import { silver_two, dark_grey_blue, color_shades_darker, color_colors_ocean } from '../../../../../../shared/constants/colors';
import { CopyIdButton, TextLinkButton } from '../../../../../../shared/scenes/organizationSettings/components/MedicalIntegrationConfigSettings';
import {
    CORE_PROCEDURE_ROLES,
    CoreProcedureRole,
    ProcedureTypeEntry,
    ProcedureTypeProposalRow,
    applyRoleToEntry,
    assignCoreRoles,
    buildCoreAssignments,
    buildProcedureTypeProposal,
    flagIsSet,
    formatProcedureTypesJson,
    getProposalWarnings,
    parseProcedureTypes,
    proposalToEntries,
    setEntryFlag
} from '../../../../../../shared/utils/procedureTypeProposal';
import { ModuleFieldLocation, findModuleField, updateModuleFieldAndFinalize } from '../../../../../../shared/utils/moduleFieldFinalize';
import { ToolbarSelect } from './PmsLookupPanels';
import { MetricInfoTooltip } from '../../../basicOrganizationApplications/components/MetricInfoTooltip';

const ASSISTANT_COLUMN_HELP = {
    name: 'Assistant',
    description: "Checked appointment types are the ones your assistant can book for callers. Unchecked types stay in your system but the assistant won't offer or schedule them.",
    calculation: ''
};

const PROCEDURE_TYPES_VARIABLE = '{procedureTypes}';

interface ProcedureTypesProposalProps {
    pmsProcedureTypes: PmsProcedureTypeModel[];
    officeId: string;
    // All of the org's apps; the templating app is the Shared Configuration
    locationApps: ApplicationModel[];
    // Called after a save so the Office Connection tab can reload that location's values
    onSaved?: (appId: string, isShared: boolean) => void;
}

interface FieldTarget {
    app: ApplicationModel;
    field: ModuleFieldLocation;
    isShared: boolean;
}

const ProcedureTypesProposal: React.FC<ProcedureTypesProposalProps> = ({ pmsProcedureTypes, officeId, locationApps, onSaved }) => {
    const locations = useMemo(() => locationApps
        .filter(app => !app.allowsTemplating)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')), [locationApps]);
    const sharedApp = locationApps.find(app => app.allowsTemplating);

    const matchingLocation = locations.find(app => app.pmsLocationId && app.pmsLocationId === officeId);
    const [locationId, setLocationId] = useState<string>(matchingLocation?.id ?? '');
    const [target, setTarget] = useState<FieldTarget | null>(null);
    const [isLoadingTarget, setIsLoadingTarget] = useState(false);
    const [targetError, setTargetError] = useState<string | null>(null);
    const [existingIsInvalid, setExistingIsInvalid] = useState(false);
    const [rows, setRows] = useState<ProcedureTypeProposalRow[]>([]);
    const [showJson, setShowJson] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveErrors, setSaveErrors] = useState<string[]>([]);
    const [savedMessage, setSavedMessage] = useState<string | null>(null);
    // Only the latest location read may update the target and proposal, so a slow read for a previous location can't win
    const loadGeneration = useRef(0);

    // Follow the office picker to its location when the location's PMS ID matches
    useEffect(() => {
        if (matchingLocation) setLocationId(matchingLocation.id);
    }, [officeId]);

    // One list of every type; the suggested new/existing patient cleaning and emergency types are marked on their rows
    const buildProposal = (existing: ProcedureTypeEntry[]) => {
        setRows(assignCoreRoles(buildProcedureTypeProposal(pmsProcedureTypes, existing), buildCoreAssignments(pmsProcedureTypes, existing)));
    };

    // Read the current value from the location, falling back to the Shared Configuration
    const loadTarget = async (appId: string) => {
        const generation = ++loadGeneration.current;
        const isCurrent = () => generation === loadGeneration.current;
        setIsLoadingTarget(true);
        setTarget(null);
        setTargetError(null);
        setIsConfirming(false);
        setSaveErrors([]);
        setSavedMessage(null);
        try {
            const location = locations.find(app => app.id === appId);
            let resolved: FieldTarget | null = null;
            const locationField = location ? await findModuleField(location.id, PROCEDURE_TYPES_VARIABLE) : null;
            if (!isCurrent()) return;
            if (locationField && locationField.resultType !== 'Ok') {
                setTargetError(`Couldn't read ${location?.name}'s modules: ${locationField.errors?.join(', ') || 'Unknown error'}`);
            } else if (locationField?.data) {
                resolved = { app: location, field: locationField.data, isShared: false };
            } else if (sharedApp) {
                const sharedField = await findModuleField(sharedApp.id, PROCEDURE_TYPES_VARIABLE);
                if (!isCurrent()) return;
                if (sharedField.resultType === 'Ok' && sharedField.data) {
                    resolved = { app: sharedApp, field: sharedField.data, isShared: true };
                }
            }
            if (!resolved && !locationField?.errors?.length) {
                setTargetError(`No Procedure Types field was found on ${location?.name ?? 'this location'} or the Shared Configuration. You can still copy the JSON.`);
            }
            setTarget(resolved);
            const existing = parseProcedureTypes(resolved?.field.value);
            setExistingIsInvalid(existing === null);
            buildProposal(existing ?? []);
        } finally {
            if (isCurrent()) setIsLoadingTarget(false);
        }
    };

    useEffect(() => {
        if (locationId) {
            loadTarget(locationId);
        } else {
            // Picking None also drops any read still in flight
            loadGeneration.current++;
            setIsLoadingTarget(false);
            setTarget(null);
            setTargetError(null);
            buildProposal([]);
        }
    }, [locationId, pmsProcedureTypes]);

    const updateRow = (index: number, changes: Partial<ProcedureTypeProposalRow>, entryChanges?: Partial<ProcedureTypeProposalRow['entry']>) => {
        setRows(rows.map((row, i) => i === index ? { ...row, ...changes, entry: { ...row.entry, ...entryChanges } } : row));
        setIsConfirming(false);
        setSavedMessage(null);
    };

    const setFlag = (index: number, flag: 'isCleaningAppointment' | 'isEmergencyType', checked: boolean) => {
        const entry = setEntryFlag(rows[index].entry, flag, checked);
        setRows(rows.map((row, i) => i === index ? { ...row, entry } : row));
        setIsConfirming(false);
    };

    // Each role belongs to one type, so giving it to this row takes it off any other row
    const setRole = (index: number, role: CoreProcedureRole | undefined) => {
        setRows(rows.map((row, i) => {
            if (i === index) {
                return { ...row, role, include: role ? true : row.include, entry: applyRoleToEntry(row.entry, row.role, role, row.pmsName) };
            }
            if (role && row.role === role) {
                return { ...row, role: undefined, entry: applyRoleToEntry(row.entry, row.role, undefined, row.pmsName) };
            }
            return row;
        }));
        setIsConfirming(false);
        setSavedMessage(null);
    };

    const entries = proposalToEntries(rows);
    const json = formatProcedureTypesJson(entries);
    const warnings = getProposalWarnings(entries);
    const unchanged = target && parseProcedureTypes(target.field.value) !== null
        && formatProcedureTypesJson(parseProcedureTypes(target.field.value)) === json;

    const apply = async () => {
        if (!target || isLoadingTarget) return;
        const savedTarget = target;
        const savedValue = JSON.stringify(entries);
        setIsSaving(true);
        setSaveErrors([]);
        const result = await updateModuleFieldAndFinalize(savedTarget.app.id, savedTarget.field, savedValue);
        setIsSaving(false);
        setIsConfirming(false);
        if (result.resultType === 'Ok') {
            setSavedMessage(`Saved Procedure Types to ${savedTarget.isShared ? 'the Shared Configuration' : savedTarget.app.name} and queued a sync. Nothing was published; publish with Sync & Publish in the Location Manager.`);
            // Only update the target this save was for; the location may have changed meanwhile
            setTarget(current => current && current.app.id === savedTarget.app.id && current.field.appliedTemplate.id === savedTarget.field.appliedTemplate.id
                && current.field.templateFormFieldId === savedTarget.field.templateFormFieldId && current.field.dynamicFormFieldName === savedTarget.field.dynamicFormFieldName
                ? { ...current, field: { ...current.field, value: savedValue } }
                : current);
            onSaved?.(savedTarget.app.id, savedTarget.isShared);
        } else {
            setSaveErrors([`Couldn't save: ${result.errors?.join(', ') || 'Unknown error'}`]);
        }
    };

    const includedCount = rows.filter(row => row.include).length;
    const bookableLabel = (id: string) => {
        const type = pmsProcedureTypes.find(t => t.id === id);
        return type?.isBookable === undefined ? '' : (type.isBookable ? 'Yes' : 'No');
    };

    return (
        <ProposalSection>
            <ProposalHeader>
                <HelpText>
                    Tick the types the assistant can book and adjust how it describes them. Active shows what the location uses today.
                </HelpText>
                <LocationPicker>
                    <span>Location</span>
                    <ToolbarSelect value={locationId} disabled={isSaving} onChange={(e) => setLocationId(e.target.value)} aria-label="Location">
                        <option value="">None (copy only)</option>
                        {locations.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
                    </ToolbarSelect>
                </LocationPicker>
            </ProposalHeader>

            {isLoadingTarget && <HelpText>Loading the current Procedure Types...</HelpText>}
            {targetError && <Notice>{targetError}</Notice>}
            {target?.isShared && (
                <Notice>
                    {locations.find(app => app.id === locationId)?.name} doesn't have its own Procedure Types, so this edits the <b>Shared Configuration</b>. That changes every location without its own value.
                </Notice>
            )}
            {existingIsInvalid && (
                <Notice>The current Procedure Types value isn't valid JSON, so the proposal starts from the PMS list. Saving will replace it.</Notice>
            )}
            {!locationId && !matchingLocation && locations.length > 0 && (
                <HelpText>No location's PMS location ID matches this office. Pick one above to merge with its current value.</HelpText>
            )}

            <TableContainer>
                <Table>
                    <thead>
                        <tr>
                            <Th style={{ width: 96 }}>
                                <input
                                    type="checkbox"
                                    title="Select all for the assistant"
                                    aria-label="Include all"
                                    checked={rows.length > 0 && includedCount === rows.length}
                                    onChange={(e) => setRows(rows.map(row => ({ ...row, include: e.target.checked })))}
                                />
                                <AssistantHeader>
                                    Assistant
                                    <MetricInfoTooltip metricKey="" customDefinition={ASSISTANT_COLUMN_HELP} iconSize={12} />
                                </AssistantHeader>
                            </Th>
                            <Th style={{ width: 170 }}>Procedure type</Th>
                            <Th style={{ minWidth: 240 }}>Description</Th>
                            <Th style={{ width: 80 }}>Minutes</Th>
                            <Th style={{ width: 60 }}>Online</Th>
                            <Th style={{ width: 70 }}>Cleaning</Th>
                            <Th style={{ width: 80 }}>Emergency</Th>
                            <Th style={{ width: 180 }}>Default for</Th>
                            <Th style={{ width: 90 }}>Status</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><Td colSpan={9}><HelpText>No procedure types.</HelpText></Td></tr>
                        )}
                        {rows.map((row, index) => (
                            <Tr key={`${row.procedureTypeId}-${index}`} excluded={!row.include}>
                                <Td>
                                    <input type="checkbox" aria-label={`Include ${row.procedureTypeId}`} checked={row.include} onChange={(e) => updateRow(index, { include: e.target.checked })} />
                                </Td>
                                <Td>
                                    <TypeName title={row.pmsName}>{row.pmsName || 'Not in PMS'}</TypeName>
                                    <IdCell>
                                        <IdText title={row.procedureTypeId}>{row.procedureTypeId}</IdText>
                                        {row.procedureTypeId && <CopyIdButton value={row.procedureTypeId} />}
                                    </IdCell>
                                </Td>
                                <Td>
                                    <CellInput
                                        value={row.entry.procedureTypeDescription ?? ''}
                                        title={row.entry.procedureTypeDescription}
                                        placeholder="Describe when callers want this"
                                        onChange={(e) => updateRow(index, {}, { procedureTypeDescription: e.target.value })}
                                    />
                                </Td>
                                <Td>
                                    <CellInput
                                        type="number"
                                        min={0}
                                        value={row.entry.appointmentDuration ?? ''}
                                        onChange={(e) => updateRow(index, {}, { appointmentDuration: e.target.value === '' ? undefined : Number(e.target.value) })}
                                    />
                                </Td>
                                <Td>{bookableLabel(row.procedureTypeId)}</Td>
                                <Td>
                                    <input type="checkbox" aria-label="Cleaning appointment" checked={flagIsSet(row.entry, 'isCleaningAppointment')} onChange={(e) => setFlag(index, 'isCleaningAppointment', e.target.checked)} />
                                </Td>
                                <Td>
                                    <input type="checkbox" aria-label="Emergency type" checked={flagIsSet(row.entry, 'isEmergencyType')} onChange={(e) => setFlag(index, 'isEmergencyType', e.target.checked)} />
                                </Td>
                                <Td>
                                    <CellSelect
                                        aria-label={`Default for ${row.procedureTypeId}`}
                                        value={row.role ?? ''}
                                        onChange={(e) => setRole(index, (e.target.value || undefined) as CoreProcedureRole | undefined)}
                                    >
                                        <option value="">None</option>
                                        {CORE_PROCEDURE_ROLES.map(definition => (
                                            <option key={definition.role} value={definition.role}>{definition.label}</option>
                                        ))}
                                    </CellSelect>
                                </Td>
                                <Td><StatusPill status={row.status}>{row.status === 'new' ? 'Not active' : row.status === 'existing' ? 'Active' : 'Not in PMS'}</StatusPill></Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
            </TableContainer>

            {warnings.length > 0 && (
                <WarningList>
                    {warnings.slice(0, 6).map(warning => <li key={warning}>{warning}</li>)}
                    {warnings.length > 6 && <li>{warnings.length - 6} more</li>}
                </WarningList>
            )}

            <JsonHeader>
                <span>{entries.length} procedure type{entries.length === 1 ? '' : 's'} selected</span>
                <JsonActions>
                    <TextLinkButton onClick={() => setShowJson(!showJson)} aria-expanded={showJson}>
                        {showJson ? 'Hide JSON' : 'Show JSON'}
                    </TextLinkButton>
                    {showJson && <CopyIdButton value={json} label="Copy JSON" />}
                </JsonActions>
            </JsonHeader>
            {showJson && <JsonPreview>{json}</JsonPreview>}

            {saveErrors.map(error => <Notice key={error} error>{error}</Notice>)}
            {savedMessage && <Notice success>{savedMessage}</Notice>}

            {/* Apply only appears once the selection differs from what's saved */}
            {target && !unchanged && entries.length > 0 && !isLoadingTarget && (
                isConfirming ? (
                    <ConfirmBar>
                        <span>
                            Save to <b>{target.isShared ? 'the Shared Configuration' : target.app.name}</b> and queue a sync? This won't publish.
                        </span>
                        <ApplyButton themes={['primary-small']} text={isSaving ? 'Saving...' : 'Save & Sync'} onClick={apply} disabled={isSaving} />
                        <TextLinkButton onClick={() => setIsConfirming(false)} disabled={isSaving}>Cancel</TextLinkButton>
                    </ConfirmBar>
                ) : (
                    <ApplyRow>
                        <ApplyButton
                            themes={['primary-small']}
                            text={target.isShared ? 'Apply to Shared Configuration' : `Apply to ${target.app.name}`}
                            onClick={() => setIsConfirming(true)}
                        />
                    </ApplyRow>
                )
            )}
        </ProposalSection>
    );
};

const ProposalSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ProposalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const LocationPicker = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
    color: ${dark_grey_blue};
`;

const AssistantHeader = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 6px;
    vertical-align: middle;
`;

const IdCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const HelpText = styled.div`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
`;

const CellSelect = styled.select`
    width: 100%;
    max-width: 200px;
    height: 30px;
    padding: 4px 8px;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    background-color: #ffffff;
    font-family: Muli;
    font-size: 12px;
    outline: none;

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }
`;

const JsonActions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const Notice = styled.div<{ error?: boolean; success?: boolean }>`
    padding: 10px 14px;
    border-radius: 8px;
    font-family: Muli;
    font-size: 13px;
    line-height: 1.4;
    color: ${props => props.error ? '#9b2c2c' : props.success ? '#276749' : '#7b5c12'};
    background-color: ${props => props.error ? 'rgba(229, 62, 62, 0.08)' : props.success ? 'rgba(56, 161, 105, 0.1)' : 'rgba(214, 158, 46, 0.12)'};
    border: 1px solid ${props => props.error ? 'rgba(229, 62, 62, 0.3)' : props.success ? 'rgba(56, 161, 105, 0.3)' : 'rgba(214, 158, 46, 0.35)'};
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    max-height: 420px;
    overflow-y: auto;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    background-color: #ffffff;
`;

const Table = styled.table`
    width: 100%;
    min-width: 1080px;
    border-collapse: collapse;
    font-family: Muli;
`;

const Th = styled.th`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #edf2f7;
    border-bottom: 1px solid ${silver_two};
    color: #4a5568;
    font-size: 13px;
    font-weight: 700;
    padding: 10px 12px;
    text-align: left;
    white-space: nowrap;
`;

const Tr = styled.tr<{ excluded: boolean }>`
    opacity: ${props => props.excluded ? 0.5 : 1};

    &:last-of-type td {
        border-bottom: none;
    }
`;

const Td = styled.td`
    padding: 8px 12px;
    border-bottom: 1px solid ${silver_two};
    font-size: 13px;
    color: #2d3748;
    vertical-align: middle;
`;

const IdText = styled.div`
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TypeName = styled.div`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
    color: #2d3748;
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 2px;
`;

const CellInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    height: 30px;
    padding: 4px 10px;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    font-family: Muli;
    font-size: 13px;
    outline: none;

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }
`;

const StatusPill = styled.span<{ status: string }>`
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    border-radius: 10px;
    padding: 2px 8px;
    white-space: nowrap;
    color: ${props => props.status === 'existing' ? '#2f855a' : props.status === 'new' ? color_shades_darker : '#b7791f'};
    background-color: ${props => props.status === 'existing' ? 'rgba(56, 161, 105, 0.12)' : props.status === 'new' ? '#edf2f7' : 'rgba(214, 158, 46, 0.15)'};
`;

const WarningList = styled.ul`
    margin: 0;
    padding-left: 20px;
    font-family: Muli;
    font-size: 12px;
    color: #b7791f;
`;

const JsonHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
`;

const JsonPreview = styled.pre`
    margin: 0;
    max-height: 260px;
    overflow: auto;
    padding: 12px 16px;
    background: #fdfdfd;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 11px;
    color: #4a5568;
    white-space: pre;
`;

const ApplyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ConfirmBar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border: 1px solid ${color_colors_ocean};
    border-radius: 8px;
    background-color: rgba(46, 125, 206, 0.05);
    font-family: Muli;
    font-size: 13px;
    color: #2d3748;
`;

const ApplyButton = styled(Button)`
    height: 34px;
    min-height: 34px;
    padding: 0 16px;
    margin: 0;
    font-size: 13px;
`;

export default ProcedureTypesProposal;
